import { PageWrapper, useProjectIntakeSubmitContext } from "@/components";
import { AddCommentModal } from "@/components/Comments/AddCommentModal";
import {
  CancelSaveDraftAndSubmitActions,
  SubmitAction,
} from "@/components/DynamicForm/DynamicSaveActions";
import { ProjectIntakeEditor } from "@/components/ProjectIntakeEditor";
import { ApproveAndRejectActions } from "@/components/ProjectIntakeEditor/ApproveRejectActions";
import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import { flattenProjectScheduleMilestones } from "@/containers/Project/helper";
import { CommentTarget, PROJECT_STATUS, RIGHTS } from "@/enums";
import { isUserApprovalStateUpToDate, routes } from "@/helpers";
import { handleServerErrorResponse } from "@/helpers/handleServerErrorResponse";
import {
  useIsAdmin,
  useIsProjectLeader,
  useIsTeamMember,
} from "@/helpers/permissions";
import { useAllRights } from "@/hooks";
import { useUserContext } from "@/providers";
import {
  useGetApprovalStateQuery,
  useGetProjectQuery,
  useSaveProjectIntakeMutation,
  useSaveProjectMutation,
  useSetApprovalStatusMutation,
  useSubmitProjectIntakeMutation,
  useUpdateProjectIntakeMutation,
} from "@/services";
import getChangedFields from "../utils/getChangedFields";
import { applicationErrorActions } from "@/store";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "antd";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 } from "uuid";

export const EditViewProjectIntake = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const { userToken, hasRight } = useUserContext();
  const submitProvider = useProjectIntakeSubmitContext();
  const rights = useAllRights();
  const placeholderId = v4();
  const formRef = useRef(null);

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [isApprovalLoading, setApprovalLoading] = useState(false);
  const [isRejectedLoading, setRejectedLoading] = useState(false);
  const [isIntakeActive, setIsIntakeActive] = useState(false);
  const [inApproval, setInApproval] = useState(false);
  const [isNextApprover, setIsNextApprover] = useState(false);
  const [isAnyApprover, setIsAnyApprover] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [canToggleSecure, setCanToggleSecure] = useState(false);
  const [intake, setIntake] = useState({});
  const [isMilestoneApprover, setIsMilestoneApprover] = useState(false);

  const {
    data: projectIntake,
    isFetching: projectFetching,
    refetch: refetchProject,
    isError: isGetProjectError,
  } = useGetProjectQuery(projectId, {
    refetchOnMountOrArgChange: true,
    skip: !projectId,
  });
  const {
    data: approvalState,
    isFetching: approvalStateLoading,
    refetch: refetchApprovalState,
    isError: isProjectError,
    error,
  } = useGetApprovalStateQuery(projectId, {
    skip: !projectIntake?.status || !projectId,
  });
  const [saveProject, { isLoading: savingProject }] = useSaveProjectMutation();
  const [setApprovalStatus] = useSetApprovalStatusMutation();
  const [saveProjectIntake, { isLoading: isIntakeSaving }] =
    useSaveProjectIntakeMutation();
  const [updateProjectIntake, { isLoading: isIntakeUpdating }] =
    useUpdateProjectIntakeMutation();
  const [submitProjectIntake, { isLoading: isSubmitting }] =
    useSubmitProjectIntakeMutation();
  const isAdmin = useIsAdmin();
  const isProjectLeader = useIsProjectLeader(projectFetching, intake);
  const isTeamMember = useIsTeamMember(projectFetching, intake);
  const flattenedMilestones = flattenProjectScheduleMilestones(projectIntake);
  const isLoading = projectFetching || !intake;

  useEffect(() => {
    if (flattenedMilestones) {
      var hasMilestoneRecord = flattenedMilestones.some((milestone) =>
        milestone?.approvers?.some((approver) =>
          approver?.assignedUsers?.some(
            (user) => user.userId === userToken.user.id
          )
        )
      );
      setIsMilestoneApprover(hasMilestoneRecord);
    }
  }, [flattenedMilestones, userToken.user.id]);

  useEffect(() => {
    if (!isProjectError) return;
    dispatch(applicationErrorActions.setApplicationError({ response: error }));
  });

  useEffect(() => {
    if (isGetProjectError) {
      navigate(routes.projects.list);
    }
  }, [projectId, navigate, projectIntake, isGetProjectError]);

  useEffect(() => {
    if (!projectId) {
      if (!intake.id) {
        setIntake({
          id: placeholderId,
          isNew: true,
          status: PROJECT_STATUS.intake.draft.id,
        });
      }
    } else {
      setIntake(projectIntake);
    }
  }, [projectId, projectIntake, placeholderId]);

  useEffect(() => {
    if (!intake || intake.isDeleted) return;

    const projectInProgress = !intake.isProjectComplete;
    setIsNextApprover(approvalState?.canUserApproveIntake);
    setIsAnyApprover(approvalState?.isCurrentUserIntakeApprover);

    const isRequestor = userToken.user.id === intake.requestor;
    const isDraft = intake.status === PROJECT_STATUS.intake.draft.id;
    setCanSubmit(
      (projectInProgress && isDraft) ||
        isNextApprover ||
        isProjectLeader ||
        isTeamMember ||
        isRequestor ||
        isAdmin ||
        isMilestoneApprover
    );

    setCanToggleSecure(
      isProjectLeader || userToken.user.id === intake.requestor
    );
  }, [
    approvalState?.canUserApproveIntake,
    approvalState?.isCurrentUserIntakeApprover,
    isNextApprover,
    projectFetching,
    intake,
    userToken,
    isTeamMember,
    isProjectLeader,
    isAdmin,
    isMilestoneApprover,
  ]);

  useEffect(() => {
    if (!intake?.status) return;

    const isInApproval = intake.status === PROJECT_STATUS.intake.inApproval.id;
    const isDraft = intake.status === PROJECT_STATUS.intake.draft.id;
    const isActiveProject = intake.status === PROJECT_STATUS.project.active.id;

    setIsIntakeActive(isDraft || isInApproval || isActiveProject);
    setInApproval(isInApproval);
  }, [intake?.status]);

  useEffect(() => {
    const workflowHistory = intake?.workflowHistory;

    if (!approvalState || projectFetching || !workflowHistory) return;

    setApprovalLoading(false);

    if (isUserApprovalStateUpToDate(workflowHistory, approvalState)) return;

    refetchApprovalState();
  }, [
    approvalState,
    projectFetching,
    intake?.workflowHistory,
    refetchApprovalState,
  ]);

  const handleSaveSubmit = (form, isProjectIntake) => {
    const data = { ...intake, ...form };
    let fileUploadError = false;
    if (
      data?.multipleFileAttachmentsNotRequired?.length > 0 ||
      data?.multipleFileAttachmentsRequired?.length > 0
    ) {
      data?.multipleFileAttachmentsNotRequired?.forEach((file) => {
        if (!file?.url) {
          fileUploadError = true;
          toast.error(
            intl.formatMessage({ id: "general.uploadsWaitForAllToComplete" })
          );
          return;
        }
      });
      data?.multipleFileAttachmentsRequired?.forEach((file) => {
        if (!file?.url) {
          fileUploadError = true;
          toast.error(
            intl.formatMessage({ id: "general.uploadsWaitForAllToComplete" })
          );
          return;
        }
      });
    }

    let request;
    if (data?.department && (data?.tenantId || data?.costCenterTenantId)) {
      const department = data.department.split(" - TenantID: ")[0];
      data.department = department;
    }

    if (isDraft() && !fileUploadError) {
      if (!isProjectIntake) {
        request = submitProjectIntake(data)
          .unwrap()
          .then(({ id }) => {
            toast.success(
              intl.formatMessage({ id: "projectIntake.submitted" })
            );
            const newIntake = !projectIntake?.status;
            if (newIntake) {
              const newRoute = routes.projects.intakeView(id);
              navigate(newRoute);
            } else {
              refetchProject();
              refetchApprovalState();
            }
          });
      } else if (!fileUploadError) {
        const changes = intake.isNew
          ? null
          : getChangedFields(data, projectIntake);
        request = (
          intake.isNew
            ? saveProjectIntake(data)
            : updateProjectIntake({ id: intake.id, changes })
        )
          .unwrap()
          .then(() => {
            toast.success(() => (
              <div>
                {intl.formatMessage({ id: "projectIntake.saved" })}
                {intake.isNew && (
                  <>
                    &nbsp;
                    <Link
                      to={routes.projects.intakeView(intake.id)}
                      data-test-id="project-intake-click-to-view"
                    >
                      {intl.formatMessage({
                        id: "projectIntake.viewIntake",
                      })}
                    </Link>
                  </>
                )}
              </div>
            ));
            if (intake.isNew) {
              navigate(routes.projects.list);
            }
          });
      }

      request.catch((data) => {
        const validationErrors = data?.errors;

        if (!validationErrors) return;

        toast.error(
          intl.formatMessage(
            { id: "projectIntake.validationError" },
            {
              fieldNames: Array.from(
                new Set(
                  validationErrors.map(({ propertyName }) => propertyName)
                )
              ).join(", "),
            }
          )
        );
      });
    } else if (!fileUploadError) {
      saveProject({ ...getChangedFields(data, projectIntake), id: intake.id })
        .unwrap()
        .then(() => {
          toast.success(intl.formatMessage({ id: "projectIntake.saved" }));
        })
        .then(() => {
          refetchProject();
        })
        .catch((response) => {
          handleServerErrorResponse(response, intl);
        });
    }
  };

  const handleReject = (callback) => {
    setRejectedLoading(true);
    setApprovalStatus({
      projectId: projectId,
      workflowStepId: approvalState.currentApprovalStepId,
      status: "rejected",
    })
      .unwrap()
      .then(() => {
        setRejectedLoading(false);
        toast.success(
          intl.formatMessage(
            { id: "projectIntake.rejectSuccess" },
            { name: intake?.projectName || "" }
          )
        );
        callback();
        refetchProject();
      })
      .catch(() => {
        setRejectedLoading(false);
        toast.error(
          intl.formatMessage(
            { id: "projectIntake.rejectError" },
            { name: intake?.projectName || "" }
          )
        );
      });
  };

  const handleApprove = () => {
    setApprovalLoading(true);
    setApprovalStatus({
      projectId: projectId,
      workflowStepId: approvalState.currentApprovalStepId,
      status: "approved",
    })
      .unwrap()
      .then(() => {
        refetchProject();

        toast.success(
          intl.formatMessage(
            { id: "projectIntake.approveSuccess" },
            { name: intake?.projectName || "" }
          )
        );
      })
      .catch(() => {
        setApprovalLoading(false);
        toast.error(
          intl.formatMessage(
            { id: "projectIntake.approveError" },
            { name: intake?.projectName || "" }
          )
        );
      });
  };

  const pageTitle = () => {
    if (intake?.projectNumber && intake?.projectName) {
      return intl.formatMessage(
        { id: "projectIntake.tabTitle" },
        { id: intake?.projectNumber, name: intake?.projectName }
      );
    }
    return intl.formatMessage({ id: "projectIntake.title" });
  };

  const isDraft = () => intake.status === PROJECT_STATUS.intake.draft.id;

  const isDisabled = () => {
    if (isDraft()) {
      return intake.isProjectComplete && !rights.canEditCompletedProject;
    }
    return true;
  };

  if (isLoading) return <SpinLoader />;

  return (
    <PageWrapper
      pageTitle={pageTitle()}
      backButtonRoute={
        projectId ? routes.projects.get(projectId) : routes.projects.list
      }
      noStyle
    >
      {intake && (
        <ProjectIntakeEditor
          intakeData={intake}
          formDisabled={isDisabled()}
          canChangeFields={canSubmit}
          onFinish={handleSaveSubmit}
          isSubmitting={isSubmitting}
          placeholderId={intake?.isNew ? intake.id : null}
          formRef={intake?.isNew ? formRef : null}
          showComments={!intake?.isNew}
          isUserAdminOrProjectLeader={isAdmin || isProjectLeader}
          canToggleSecure={isAdmin || canToggleSecure}
          isNewSubmission={isDraft()}
          leftActions={
            <>
              {projectId && (
                <Link to={routes.projects.get(projectId)}>
                  <Button
                    icon={<FontAwesomeIcon icon={faArrowLeft} />}
                    data-test-id="intake-navigate-to-project"
                  >
                    {intl.formatMessage({ id: "projects.goToProject" })}
                  </Button>
                </Link>
              )}
            </>
          }
          rightActions={
            <>
              {isIntakeActive && (
                <>
                  <>
                    {isDraft() && (
                      <CancelSaveDraftAndSubmitActions
                        formRef={submitProvider.formRef}
                        cancelButtonOnClick={() =>
                          navigate(routes.projects.list)
                        }
                        cancelButtonText={intl.formatMessage({
                          id: "general.cancel",
                        })}
                        noValidateButtonOnClick={(form) =>
                          handleSaveSubmit(form, true)
                        }
                        noValidateButtonText={intl.formatMessage({
                          id: "projectIntake.save",
                        })}
                        noValidateIsSaving={isIntakeSaving || isIntakeUpdating}
                        isSubmitting={isSubmitting}
                        submitButtonText={intl.formatMessage({
                          id: "general.submit",
                        })}
                        submitButtonOnClick={handleSaveSubmit}
                      />
                    )}
                    {!isDraft() && (
                      <SubmitAction
                        formRef={submitProvider.formRef}
                        submitButtonText={intl.formatMessage({
                          id: "projectIntake.save",
                        })}
                        isSubmitting={savingProject}
                        disabled={
                          ((inApproval &&
                            (isApprovalLoading || isRejectedLoading)) ||
                            !canSubmit) &&
                          !canToggleSecure &&
                          !hasRight([RIGHTS.editCompletedProject])
                        }
                      />
                    )}
                  </>

                  {inApproval && isAnyApprover && (
                    <ApproveAndRejectActions
                      disableActions={
                        savingProject || approvalStateLoading || !isNextApprover
                      }
                      isApprovalLoading={isApprovalLoading}
                      isRejectedLoading={isRejectedLoading}
                      approveButtonText={intl.formatMessage({
                        id: "general.approve",
                      })}
                      approveButtonOnClick={handleApprove}
                      rejectButtonOnClick={() => setShowCommentModal(true)}
                      rejectButtonText={intl.formatMessage({
                        id: "general.reject",
                      })}
                    />
                  )}
                </>
              )}
            </>
          }
        />
      )}
      {showCommentModal && (
        <AddCommentModal
          isVisible={showCommentModal}
          target={CommentTarget.IntakeProject}
          targetId={intake?.id}
          projectId={intake?.id}
          setVisible={setShowCommentModal}
          tagName={"Rejected"}
          tagDisabled={true}
          onFinish={(callback) => handleReject(callback)}
        />
      )}
    </PageWrapper>
  );
};
